import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { THEMES, applyTheme, clearTheme, loadSavedTheme } from '../themes/themes';
import type { Theme } from '../themes/themes';

export const Route = createFileRoute('/themes')({
  component: ThemeShowcase,
});

function ThemeShowcase() {
  const [selectedTheme, setSelectedTheme] = useState<number | null>(
    () => loadSavedTheme()?.id ?? null,
  );

  function handleApply(theme: Theme) {
    applyTheme(theme);
    setSelectedTheme(theme.id);
    window.location.reload();
  }

  function handleReset() {
    clearTheme();
    setSelectedTheme(null);
    window.location.reload();
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-foreground-bright mb-2">Theme Showcase</h1>
          <p className="text-foreground-muted mb-4">
            Choose your favorite dark theme. Click "Apply" to see it live across the whole app.
          </p>
          {selectedTheme && (
            <div className="flex items-center justify-center gap-3">
              <span className="text-sm text-foreground-faint">
                Active: #{selectedTheme} — {THEMES.find((t) => t.id === selectedTheme)?.name}
              </span>
              <button
                onClick={handleReset}
                className="px-3 py-1 text-xs bg-surface-raised border border-border rounded hover:bg-overlay text-foreground-muted"
              >
                Reset to Default
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {THEMES.map((theme) => (
            <ThemeCard
              key={theme.id}
              theme={theme}
              isActive={selectedTheme === theme.id}
              onApply={() => handleApply(theme)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function ThemeCard({
  theme,
  isActive,
  onApply,
}: {
  theme: Theme;
  isActive: boolean;
  onApply: () => void;
}) {
  return (
    <div
      className={`rounded-xl overflow-hidden border-2 transition-all ${
        isActive
          ? 'ring-2 ring-primary scale-[1.02]'
          : 'border-transparent hover:border-border'
      }`}
      style={{ background: theme.colors.surface, borderColor: isActive ? theme.colors.primary : undefined }}
    >
      {/* Header */}
      <div className="p-4 border-b" style={{ borderColor: theme.colors.border }}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-bold text-lg" style={{ color: theme.colors.foreground }}>
            {theme.id}. {theme.name}
          </h3>
          {isActive && (
            <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: theme.colors.primary, color: '#fff' }}>
              Active
            </span>
          )}
        </div>
        <p className="text-xs" style={{ color: theme.colors.foregroundMuted }}>
          {theme.description}
        </p>
      </div>

      {/* Color Palette */}
      <div className="p-4 space-y-3">
        {/* Backgrounds */}
        <div>
          <p className="text-xs mb-1" style={{ color: theme.colors.foregroundFaint }}>Backgrounds</p>
          <div className="flex gap-2">
            {[theme.colors.background, theme.colors.surface, theme.colors.surfaceRaised].map((bg, i) => (
              <div
                key={i}
                className="w-10 h-10 rounded border"
                style={{ background: bg, borderColor: theme.colors.border }}
              />
            ))}
          </div>
        </div>

        {/* Text */}
        <div>
          <p className="text-xs mb-1" style={{ color: theme.colors.foregroundFaint }}>Text</p>
          <div className="flex gap-2">
            {[
              { color: theme.colors.foreground, label: 'Aa', bold: true },
              { color: theme.colors.foregroundMuted, label: 'Aa', bold: false },
              { color: theme.colors.foregroundFaint, label: 'Aa', bold: false },
            ].map((t, i) => (
              <div
                key={i}
                className={`w-10 h-10 rounded border flex items-center justify-center text-xs ${t.bold ? 'font-bold' : ''}`}
                style={{ background: theme.colors.background, color: t.color, borderColor: theme.colors.border }}
              >
                {t.label}
              </div>
            ))}
          </div>
        </div>

        {/* Accents + Status */}
        <div>
          <p className="text-xs mb-1" style={{ color: theme.colors.foregroundFaint }}>Colors</p>
          <div className="flex gap-1.5 flex-wrap">
            {[theme.colors.primary, theme.colors.accent, theme.colors.success, theme.colors.warning, theme.colors.error].map((c, i) => (
              <div key={i} className="w-7 h-7 rounded" style={{ background: c }} />
            ))}
          </div>
        </div>

        {/* Sample UI */}
        <div className="pt-2 space-y-2">
          <button
            onClick={onApply}
            className="w-full px-3 py-2 rounded text-sm font-medium text-white cursor-pointer transition-opacity hover:opacity-90"
            style={{ background: theme.colors.primary }}
          >
            {isActive ? 'Applied' : 'Apply Live'}
          </button>
          <div
            className="px-3 py-2 rounded text-xs border"
            style={{
              background: theme.colors.surfaceRaised,
              color: theme.colors.foregroundMuted,
              borderColor: theme.colors.border,
            }}
          >
            Sample card content
          </div>
          <div className="flex gap-1 flex-wrap">
            {[
              { label: 'PAID', bg: theme.colors.success },
              { label: 'PENDING', bg: theme.colors.warning },
              { label: 'ERROR', bg: theme.colors.error },
            ].map((badge) => (
              <span key={badge.label} className="px-2 py-0.5 rounded text-xs text-white" style={{ background: badge.bg }}>
                {badge.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
